/**
 * @Author: qinyifang
 * @Date: 2025-04-28 14:45:30
 * @Description: 对话相关的API接口定义，包括获取对话列表、消息、重命名、删除等功能
 */
import { apiClient } from "@/assets/js/utils/axios"
import {
  conversationsApi,
  messagesApi,
  conversationNameApi,
  conversationDeleteApi,
  messageFeedbackApi,
  messageUpdateStatusApi,
  userAgentInfoApi,
  agentFaqInputOptionsApi,
  agentFaqApi,
  feedbackTagsApi,
  fileUploadApi,
  fileDeleteApi,
  asrAuthApi,
  sobotTransferToHumanApi,
  sobotSendMessageApi,
  sobotGetMessagesApi,
  sobotEndSessionApi
} from "@/api/index"
import type {
  ConversationInfo,
  ConversationNameResponse,
  MessageItem,
  AgentInfo,
  SuccessResponse,
  CustomAxiosRequestConfig,
  ContentMetadata,
  FeedbackTagsResponse,
  FileUploadResponse,
  FileDeleteRequest
} from "@/types/app"

/**
 * 获取对话列表
 * @param {string} busi_agent_id 业务代理ID（可选）
 * @param {number} offset 偏移量（可选）
 * @param {number} limit 限制数量（可选）
 * @returns {Promise<{ list: ConversationInfo[]; total: number }>} 对话列表和总数
 */
export function getConversations(
  busi_agent_id?: string,
  offset?: number,
  limit?: number
): Promise<{ list: ConversationInfo[]; total: number }> {
  const requestBody: any = { limit: limit || 10 }
  if (busi_agent_id) {
    requestBody.busi_agent_id = busi_agent_id
  }
  if (offset !== undefined) {
    requestBody.offset = offset
  }
  return apiClient
    .post<any>(conversationsApi, requestBody, { showError: false } as CustomAxiosRequestConfig)
    .then((response: any) => {
      if (Array.isArray(response)) {
        return { list: response, total: response.length }
      }
      return {
        list: response.data || [],
        total: response.total || 0
      }
    })
}

/**
 * 获取特定对话的消息(多轮历史对话)
 * @param {string} conversationId 对话ID
 * @returns {Promise<MessageItem[]>} 消息列表
 */
export function getConversationMessages(conversationId: string): Promise<MessageItem[]> {
  return apiClient
    .post<any>(messagesApi, {
      conversation_id: conversationId,
      limit: 20
    })
    .then((response: any) => {
      if (Array.isArray(response)) {
        return response as MessageItem[]
      }
      return (response.data || []) as MessageItem[]
    })
}

/**
 * 重命名对话
 * @param {string} conversationId 对话ID
 * @param {string} name 新名称
 * @returns {Promise<ConversationNameResponse>} 响应结果
 */

export function renameConversation(conversationId: string, title: string): Promise<ConversationNameResponse> {
  return apiClient
    .post<ConversationNameResponse>(conversationNameApi, {
      conversation_id: conversationId,
      title
    })
    .then((response) => response as unknown as ConversationNameResponse)
}

/**
 * 删除对话（支持单个和批量）
 * @param {string[]} conversationIds 对话ID数组
 * @returns {Promise<SuccessResponse>} 响应结果
 */
export function deleteConversations(conversationIds: string[]): Promise<SuccessResponse> {
  return apiClient.post(conversationDeleteApi, { conversation_ids: conversationIds })
}

/**
 * 获取点赞点踩标签列表
 *  @param {string} feedback点赞点踩类型 0-点踩，1-点赞
 * @returns {Promise<FeedbackTagsResponse>} 点赞点踩标签列表
 */
export function getFeedbackTags(feedback: string): Promise<FeedbackTagsResponse> {
  return apiClient.post(feedbackTagsApi, { feedback }, { showError: false } as CustomAxiosRequestConfig)
}

/**
 * 提交消息反馈
 * @param {string} messageId 消息ID
 * @param {"0" | "1" | "2"} feedback 用户反馈（点赞、点踩等）0-点踩1-点赞2-取消赞踩
 * @param {ContentMetadata} contentMetadata 点赞点踩备注（可选）
 * @returns {Promise<SuccessResponse>} 响应结果
 */
export function submitFeedback(
  messageId: string,
  feedback: "0" | "1" | "2",
  contentMetadata?: ContentMetadata
): Promise<SuccessResponse> {
  const requestBody: any = { message_id: messageId, feedback }

  if (contentMetadata) {
    requestBody.content_metadata = contentMetadata
  }

  return apiClient.post(messageFeedbackApi, requestBody)
}

/**
 * 更新消息状态（刷新对话时使用）
 * @param {string} messageId 消息ID
 * @param {string} status 状态值
 * @returns {Promise<SuccessResponse>} 响应结果
 */
export function updateMessageStatus(messageId: string, status: string): Promise<SuccessResponse> {
  return apiClient.post(messageUpdateStatusApi, {
    message_id: messageId,
    status
  })
}

/**
 * 获取智能体列表
 * @param {Object} params 分页参数
 * @param {number} params.offset 偏移量（可选）
 * @param {number} params.limit 限制数量（可选）
 * @returns {Promise<{ list: AgentInfo[]; total: number }>} 智能体列表和总数
 */
export function getUserAgentInfo(params?: {
  offset?: number
  limit?: number
}): Promise<{ list: AgentInfo[]; total: number }> {
  const requestBody: any = {
    offset: params?.offset ?? 0,
    limit: params?.limit ?? 20
  }
  return apiClient.post(userAgentInfoApi, requestBody).then((response: any) => {
    if (Array.isArray(response)) {
      return { list: response as AgentInfo[], total: response.length }
    }
    return {
      list: (response.data || []) as AgentInfo[],
      total: response.total || 0
    }
  })
}

/**
 * 获取智能体高频问题输入选项
 * @returns {Promise<any>} 输入选项配置
 */
export function getAgentFaqInputOptions(): Promise<any> {
  return apiClient
    .post(agentFaqInputOptionsApi, {}, { showError: false } as CustomAxiosRequestConfig)
    .then((response: any) => {
      return response || {}
    })
}

/**
 * 获取智能体高频问题列表
 * @param {string} busi_agent_id 业务代理ID
 * @returns {Promise<any[]>} 高频问题列表
 */
export function getAgentFaqList(busi_agent_id: string): Promise<any[]> {
  return apiClient
    .post(agentFaqApi, { busi_agent_id }, { showError: false } as CustomAxiosRequestConfig)
    .then((response: any) => {
      if (Array.isArray(response)) {
        return response
      }
      return response?.data || []
    })
}

/**
 * 上传单个文件
 * @param {File} file 要上传的文件
 * @returns {Promise<FileUploadResponse>} 上传结果，返回fileId和fileName的映射数组
 */
export function uploadFile(file: File): Promise<FileUploadResponse> {
  const formData = new FormData()
  formData.append("type", file.type || "application/octet-stream")
  formData.append("file", file)

  return apiClient
    .post<FileUploadResponse>(fileUploadApi, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    } as CustomAxiosRequestConfig)
    .then((response: any) => {
      // 单个文件上传返回单个对象
      return response as FileUploadResponse
    })
}

/**
 * 删除文件
 * @param {FileDeleteRequest} fileData 文件删除数据
 * @returns {Promise<void>} 删除结果
 */
export function deleteFile(fileData: FileDeleteRequest): Promise<void> {
  return apiClient.post(fileDeleteApi, fileData).then(() => {
    // 删除成功，不需要返回数据
  })
}

/** 腾讯云ASR鉴权信息 */
export interface AsrAuthInfo {
  secret_id: string
  secret_key: string
  app_id: string
  token?: string
}

/**
 * 获取腾讯云语音识别鉴权信息
 * @returns {Promise<AsrAuthInfo>} ASR鉴权信息
 */
export function getAsrAuth(): Promise<AsrAuthInfo> {
  return apiClient.post(asrAuthApi, {}, { showError: false } as CustomAxiosRequestConfig)
}

// ================= 智齿客服相关API =================

/** 转人工客服请求参数 */
export interface SobotTransferToHumanRequest {
  conversation_id: string
  busi_agent_id: string
}

/** 转人工技能组项 */
export interface SobotGroupItem {
  groupid?: string
  groupname?: string
  [key: string]: any
}

/** 转人工客服响应 */
export interface SobotTransferToHumanResponse {
  conversationId?: string
  sobotResponse?: {
    item?: {
      // 状态码: -1-重复请求拒绝, 0-排队中, 1-成功分配客服,
      // 2-无客服在线, 3-用户在黑名单, 4-待分配(异步模式), 6-需要选择技能组
      status: number
      visitorid?: string
      cid?: string
      agent_name?: string // status=1 返回
      agent_face?: string // status=1 返回
      agentid?: string // status=1 返回
      count?: number | null // status=0 返回, 排队位置
      queue_word?: string | null // status=0 返回, 排队说辞
      service_prompt_word?: string // status=1 返回, 接入成功提示语
      admin_hello_word?: string // status=1 返回, 人工客服欢迎语
      offline_word?: string // status=2 返回, 客服不在线说辞
      allocated_word?: string // status=4 返回, 进入待分配列表说辞
      group_list?: SobotGroupItem[] // status=6 返回, 分组接待技能组列表
      router_rule_name?: string // 命中的智能路由规则名称(仅智能路由触发)
      reception_schemeid?: string // v6接待方案ID
      reception_scheme_name?: string // v6接待方案名称
    }
    ret_code?: string
    ret_msg?: string
    status?: number
    cid?: string
  }
}

/** 发送消息到智齿客服请求 */
export interface SobotSendMessageRequest {
  conversation_id: string
  message: string
  busi_agent_id: string
  file_ids?: string[] // 文件ID列表（图片/文件，复用现有AI文件上传逻辑）
}

/** 查询客服消息响应 */
export interface SobotMessageItem {
  id: number // 消息序号 ID，用作下一次轮询的 msgid
  msgCode?: string // 消息码，如 "202"
  msgid?: string // 智齿消息ID（字符串）
  msgType?: string // 消息类型: TEXT, IMAGE, FILE 等
  content: string // 消息内容
  agentid?: string // 客服ID
  agentNick?: string // 客服昵称
  agentFace?: string // 客服头像
  partnerid?: string
  cid?: string // 智齿会话ID
  revokeMsgid?: string | null
  status?: string | null
  overTimeType?: string | null
  conversationId?: string
  messageId?: string
  rawContent?: string
  createdTime?: string
  /** @deprecated 兼容老版本字段 */
  message_id?: string
  /** @deprecated 兼容老版本字段 */
  message_type?: string
  /** @deprecated 兼容老版本字段 */
  sender_type?: "user" | "agent"
  /** @deprecated 兼容老版本字段 */
  created_time?: string
  /** @deprecated 兼容老版本字段 */
  file_url?: string
}

/** 结束会话请求 */
export interface SobotEndSessionRequest {
  conversation_id: string
}

/** 发送消息响应 */
export interface SobotSendMessageResponse {
  status?: string // "1"-成功, "2"-会话结束或异常, "0"-失败 等
  ret_code?: string
  ret_msg?: string
  msgid?: string
}

/**
 * 请求人工客服
 * @param data 转人工请求参数
 * @returns {Promise<SobotTransferToHumanResponse>} 转人工响应
 */
export function sobotTransferToHuman(data: SobotTransferToHumanRequest): Promise<SobotTransferToHumanResponse> {
  return apiClient.post(sobotTransferToHumanApi, data, { showError: false } as CustomAxiosRequestConfig)
}

/**
 * 发送消息到智齿客服
 * @param data 消息请求参数
 * @returns {Promise<SobotSendMessageResponse>} 发送结果
 */
export function sobotSendMessage(data: SobotSendMessageRequest): Promise<SobotSendMessageResponse> {
  return apiClient.post(sobotSendMessageApi, data, { showError: false } as CustomAxiosRequestConfig)
}

/**
 * 查询客服消息
 * @param cid 智齿客服会话ID
 * @param msgid 最新消息序号 ID（用作增量拉取标记），首次拉取可不传
 * @returns {Promise<SobotMessageItem[]>} 客服消息列表
 */
export function sobotGetMessages(cid: string, msgid?: number): Promise<SobotMessageItem[]> {
  const body: Record<string, any> = { cid }
  if (msgid != null) {
    body.msgid = msgid
  }
  return apiClient
    .post(sobotGetMessagesApi, body, { showError: false } as CustomAxiosRequestConfig)
    .then((res: any) => {
      if (Array.isArray(res)) return res
      return res?.data || []
    })
}

/**
 * 结束人工客服会话
 * @param data 结束会话请求参数
 * @returns {Promise<SuccessResponse>} 结束结果
 */
export function sobotEndSession(data: SobotEndSessionRequest): Promise<SuccessResponse> {
  return apiClient.post(sobotEndSessionApi, data, { showError: false } as CustomAxiosRequestConfig)
}
